import type { QgammonEloPoint } from '@/types/qgammon';

/**
 * La scheda «Statistiche» della Hub backgammon (s3, scelta di Luca 25/09): il grafico dell'andamento
 * ELO in testa e le voci di `/me/stats` in due colonne. Qui la parte pura — cosa si disegna e cosa si
 * scrive — perche' il test la esegua senza montare niente.
 *
 * ⛔ **Niente dati inventati**: una voce il cui campo il server non da' (assente, `null`, non numero)
 * NON si mostra — ne' a zero ne' a trattino. Uno zero scritto al posto di un campo mancante e' un
 * vuoto affermato su una domanda che il server non ha risposto (CLAUDE.md, «Gli 8 tab della Hub»).
 * Il caso vero oggi e' `avgPr: null` (nessun match con PR calcolato); gli altri campi il contratto li
 * da' sempre, ma un server piu' vecchio o piu' nuovo del contratto non deve far scrivere un numero finto.
 */

/** Una voce della griglia: la chiave della copy e il valore, nella forma che la voce richiede. */
export type ValoreVoce =
  /** Un conteggio: giocate, vinte, perse. `tono` colora vinte/perse. */
  | { tipo: 'numero'; n: number; tono: 'neutro' | 'vinte' | 'perse' }
  /** Vinte / perse: gammon e backgammon — contano le PARTITE, non i match (tipi in `types/qgammon`). */
  | { tipo: 'coppia'; vinte: number; perse: number }
  /** Vinte su giocate: contro umani, contro bot. */
  | { tipo: 'su'; vinte: number; giocate: number }
  /**
   * PR medio: un numero con una cifra decimale; piu' basso e' meglio, quindi nessun colore.
   * `partite` e' il campione (`prMatches`) SOLO quando il server lo da' come numero > 0 — assente
   * o `null` (server piu' vecchio del contratto #84) lascia la voce esattamente com'era prima.
   */
  | { tipo: 'pr'; pr: number; partite?: number };

export type ChiaveVoce =
  | 'played'
  | 'won'
  | 'lost'
  | 'gammons'
  | 'avgPr'
  | 'vsHumans'
  | 'vsBots'
  | 'backgammons';

export interface Voce {
  chiave: ChiaveVoce;
  valore: ValoreVoce;
}

const num = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);
const campo = (o: unknown, k: string): unknown =>
  o !== null && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined;

/**
 * Le voci in due colonne, nell'ordine dell'immagine s3: a sinistra il risultato (giocate, vinte,
 * perse, gammon, PR medio), a destra contro chi (umani, bot) e i backgammon.
 * Riceve `unknown` di proposito: la risposta e' quella del server, non quella del tipo.
 */
export function vociStatistiche(stats: unknown): { sinistra: Voce[]; destra: Voce[] } {
  const sinistra: Voce[] = [];
  const destra: Voce[] = [];
  const conteggio = (chiave: ChiaveVoce, k: string, tono: 'neutro' | 'vinte' | 'perse') => {
    const n = campo(stats, k);
    if (num(n)) sinistra.push({ chiave, valore: { tipo: 'numero', n, tono } });
  };
  conteggio('played', 'played', 'neutro');
  conteggio('won', 'won', 'vinte');
  conteggio('lost', 'lost', 'perse');

  const coppia = (lato: Voce[], chiave: ChiaveVoce, kVinte: string, kPerse: string) => {
    const vinte = campo(stats, kVinte);
    const perse = campo(stats, kPerse);
    // Mezza coppia non si scrive: «4 / —» direbbe un numero su una meta' che il server non ha dato.
    if (num(vinte) && num(perse)) lato.push({ chiave, valore: { tipo: 'coppia', vinte, perse } });
  };
  coppia(sinistra, 'gammons', 'gammonsWon', 'gammonsLost');

  const pr = campo(stats, 'avgPr');
  if (num(pr)) {
    const prMatches = campo(stats, 'prMatches');
    const valore: ValoreVoce =
      num(prMatches) && prMatches > 0 ? { tipo: 'pr', pr, partite: prMatches } : { tipo: 'pr', pr };
    sinistra.push({ chiave: 'avgPr', valore });
  }

  const su = (chiave: ChiaveVoce, k: string) => {
    const o = campo(stats, k);
    const vinte = campo(o, 'won');
    const giocate = campo(o, 'played');
    if (num(vinte) && num(giocate)) destra.push({ chiave, valore: { tipo: 'su', vinte, giocate } });
  };
  su('vsHumans', 'vsHumans');
  su('vsBots', 'vsBots');
  coppia(destra, 'backgammons', 'backgammonsWon', 'backgammonsLost');

  return { sinistra, destra };
}

/**
 * «Nessuna partita classificata»: il server ha risposto e non c'e' niente da contare. Vale anche il
 * `404 not_found` di `/me/stats` (l'utente non ha ancora una riga in QGammon: non ha mai giocato),
 * che il pannello traduce in questo stesso caso prima di chiamarla.
 * ⚠️ `played` assente NON e' zero: senza il campo non so, e il vuoto non si afferma.
 */
export function statisticheVuote(stats: unknown): boolean {
  const played = campo(stats, 'played');
  return num(played) && played === 0;
}

export interface PuntoElo {
  elo: number;
  /** La compensazione di un match annullato dall'admin (`kind: 'void'`): spiega un salto nel grafico. */
  annullato: boolean;
}

/**
 * I punti del grafico, dal piu' VECCHIO al piu' recente (il server li da' al contrario).
 * Il primo punto e' l'ELO di PARTENZA della riga piu' vecchia (`eloBefore`), poi l'ELO dopo ogni riga
 * (`eloAfter`): N righe disegnano N+1 punti, e la prima variazione si vede. Le righe `void` restano —
 * il contratto le tiene visibili apposta — marcate `annullato`. Una riga senza numeri validi si scarta.
 */
export function andamentoElo(points: readonly QgammonEloPoint[]): PuntoElo[] {
  const valide = points.filter((p) => num(p.eloAfter) && num(p.eloBefore));
  if (valide.length === 0) return [];
  const cronologico = [...valide].reverse();
  return [
    { elo: cronologico[0].eloBefore, annullato: false },
    ...cronologico.map((p) => ({ elo: p.eloAfter, annullato: p.kind === 'void' })),
  ];
}
